import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { SiteRagReport } from '../../../types/siteRag';
import { walkCitationText } from '../../../lib/walkCitationText';

/** Render saved narrative structure without enabling HTML or losing source links. */
export function CitedReportProse({ text, references, renderText }: {
  text: string;
  references: SiteRagReport['references'];
  renderText?: (text: string) => React.ReactNode;
}) {
  const cite = renderText ?? ((value: string) => walkCitationText(value, references));
  const plainText = (children: React.ReactNode): string => React.Children.toArray(children).map(child =>
    typeof child === 'string' ? child : React.isValidElement<{ children?: React.ReactNode }>(child) ? plainText(child.props.children) : '').join('');
  function citedChildren(children: React.ReactNode): React.ReactNode {
    return React.Children.map(children, child => {
      if (typeof child === 'string') return cite(child);
      if (React.isValidElement<{ children?: React.ReactNode }>(child)) {
        // A Ref label must resolve through the report's source list, even if
        // saved Markdown wrapped it in an unrelated destination.
        if (child.type === 'a') return /\[Ref\s+\d+\]/i.test(plainText(child.props.children)) ? citedChildren(child.props.children) : child;
        if (child.type === 'code') return child;
        return React.cloneElement(child, {}, citedChildren(child.props.children));
      }
      return child;
    });
  }
  const block = (tag: 'p' | 'li' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6') =>
    ({ children }: { children?: React.ReactNode }) => React.createElement(tag, {}, citedChildren(children));
  return <div className="space-y-3 [&_p]:mb-3 [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h4]:font-semibold [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:my-1">
    <ReactMarkdown skipHtml components={{ img: ({ alt }) => <span>{alt ?? ''}</span>, p: block('p'), li: block('li'), h1: block('h1'), h2: block('h2'),
      h3: block('h3'), h4: block('h4'), h5: block('h5'), h6: block('h6') }}>{text}</ReactMarkdown>
  </div>;
}
